import { useState, useCallback } from 'react';
import { supportFaqApi } from '../api/supportFaq';
import type { SupportFaqEntry, SupportQueryLog, TopQuestion, SupportWidgetSettings } from '../api/supportFaq';

export function useSupportFaq() {
  const [entries, setEntries] = useState<SupportFaqEntry[]>([]);
  const [queries, setQueries] = useState<SupportQueryLog[]>([]);
  const [topQuestions, setTopQuestions] = useState<TopQuestion[]>([]);
  const [settings, setSettings] = useState<SupportWidgetSettings | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [lastPage, setLastPage] = useState(1);

  const fetchEntries = useCallback(async (p = 1) => {
    setLoading(true);
    setError(null);
    try {
      const res = await supportFaqApi.list(p);
      setEntries(res.data.data);
      setTotal(res.data.meta.total);
      setLastPage(res.data.meta.last_page);
      setPage(p);
    } catch (err: any) {
      setError(err?.message ?? 'Failed to load FAQ entries.');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchQueries = useCallback(async (p = 1) => {
    try {
      const res = await supportFaqApi.queries(p);
      setQueries(res.data.data);
    } catch {
      // best-effort — the main FAQ list is the primary content on this page
    }
  }, []);

  const fetchTopQuestions = useCallback(async () => {
    try {
      const res = await supportFaqApi.topQuestions();
      setTopQuestions(res.data.data);
    } catch {
      // best-effort
    }
  }, []);

  const fetchSettings = useCallback(async () => {
    try {
      const res = await supportFaqApi.settings();
      setSettings(res.data.data);
    } catch (err: any) {
      setError(err?.message ?? 'Failed to load widget settings.');
    }
  }, []);

  const createEntry = useCallback(async (payload: { question: string; answer: string }) => {
    setSaving(true);
    setError(null);
    try {
      const res = await supportFaqApi.create(payload);
      setEntries((prev) => [res.data.data, ...prev]);
      return res.data.data;
    } catch (err: any) {
      setError(err?.message ?? 'Failed to create FAQ entry.');
      return null;
    } finally {
      setSaving(false);
    }
  }, []);

  const updateEntry = useCallback(async (id: number, payload: Partial<Pick<SupportFaqEntry, 'question' | 'answer' | 'is_active' | 'sort_order'>>) => {
    try {
      const res = await supportFaqApi.update(id, payload);
      setEntries((prev) => prev.map((e) => (e.id === id ? res.data.data : e)));
      return res.data.data;
    } catch (err: any) {
      setError(err?.message ?? 'Failed to update FAQ entry.');
      return null;
    }
  }, []);

  const deleteEntry = useCallback(async (id: number) => {
    try {
      await supportFaqApi.destroy(id);
      setEntries((prev) => prev.filter((e) => e.id !== id));
    } catch (err: any) {
      setError(err?.message ?? 'Failed to delete FAQ entry.');
    }
  }, []);

  const saveSettings = useCallback(async (payload: SupportWidgetSettings) => {
    setSaving(true);
    setError(null);
    try {
      const res = await supportFaqApi.updateSettings(payload);
      setSettings(res.data.data);
      return res.data.data;
    } catch (err: any) {
      setError(err?.message ?? 'Failed to save widget settings.');
      return null;
    } finally {
      setSaving(false);
    }
  }, []);

  return {
    entries, queries, topQuestions, settings, loading, saving, error, total, page, lastPage,
    fetchEntries, fetchQueries, fetchTopQuestions, fetchSettings,
    createEntry, updateEntry, deleteEntry, saveSettings, setError,
  };
}
